import {
  ActiveSourcingChannelDetails,
  SourcingChannelStatus,
} from 'api-clients/monolith';
import { create } from 'zustand';

type State = {
  lastAutomationChannel?: ActiveSourcingChannelDetails;
  setLastAutomationChannel: (channels: ActiveSourcingChannelDetails[]) => void;
  selectedChannel?: ActiveSourcingChannelDetails;
  setSelectedChannel: (channel?: ActiveSourcingChannelDetails) => void;
  opening?: {
    id: string;
    title: string;
  };
  setOpening: ({ id, title }: { id: string; title: string }) => void;
  refetchFunctions: (() => Promise<void>)[];
  addRefetchFunction: (refetchFn: () => Promise<void>) => void;
  refetchOnCampaignUpdate: () => Promise<void>;
  refetchReachChannels?: () => Promise<void>;
  setRefetchReachChannels: (refetchReachChannels: () => Promise<void>) => void;
  activeCampaigns: ActiveSourcingChannelDetails[];
  setActiveCampaigns: (campaigns: ActiveSourcingChannelDetails[]) => void;
  setActiveCampaignStatus: (
    sourcingPurchaseId: string,
    status: SourcingChannelStatus,
  ) => void;
};

export const useChannelsStore = create<State>((set, get) => ({
  lastAutomationChannel: undefined,
  setLastAutomationChannel: channels => {
    const lastAutomationChannel = channels
      .filter(c => c.automation_triggered_at)
      .sort(
        (a, b) =>
          Date.parse(b.automation_triggered_at!) -
          Date.parse(a.automation_triggered_at!),
      )[0];

    set({ lastAutomationChannel });
  },
  selectedChannel: undefined,
  setSelectedChannel: selectedChannel => set({ selectedChannel }),
  opening: undefined,
  setOpening: opening => set({ opening }),

  /**
   * When campaign parameters are updated, we need to refetch opening details, analytics and sourcing_channels APIs
   * These refetch functions are added to the array and executed at once when the campaign is updated
   */
  refetchFunctions: [],
  addRefetchFunction: refetchFn =>
    set(state => ({
      refetchFunctions: [...state.refetchFunctions, refetchFn],
    })),
  refetchOnCampaignUpdate: async () => {
    await Promise.all(get().refetchFunctions.map(fn => fn()));
  },

  refetchReachChannels: undefined,
  setRefetchReachChannels: refetchReachChannels =>
    set({ refetchReachChannels }),

  activeCampaigns: [],
  setActiveCampaigns: activeCampaigns => set({ activeCampaigns }),
  setActiveCampaignStatus: (
    sourcingPurchaseId: string,
    status: SourcingChannelStatus,
  ) => {
    set(state => ({
      activeCampaigns: state.activeCampaigns.map(campaign =>
        campaign.sourcing_purchase_id === sourcingPurchaseId
          ? { ...campaign, status }
          : campaign,
      ),
    }));
  },
}));
